import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../common/Card/Card';
import Button from '../../common/Button/Button';
import Notification from '../../common/Notification/Notification';
import BoundingBoxMap from '../../common/BoundingBoxMap/BoundingBoxMap';
import QueueInfo from '../../common/QueueInfo';
import { useSearch } from '../../../hooks/useSearch';
import { useAuth } from '../../../hooks/useAuth';
import { TaskStatus } from '../../../types/search.types';
import {
  Search,
  Clock,
  Map,
  MapPin,
  Calendar,
  Building2,
  X,
  ArrowLeft,
  RefreshCw,
  Info,
  ChevronDown,
  ChevronUp,
  Trash2,
  User,
  Mail,
  CheckCircle,
  Download,
  Megaphone,
} from 'lucide-react';
import SearchStepper from './SearchStepper';
import HoverTooltip from '../../common/HoverTooltip/HoverTooltip';
import {
  getSearchPipeline,
  getSearchProgressPercent,
} from './searchPipelineConfig';
import CompletedNoWebsiteSummary, { getCreationNotificationMessage } from './CompletedNoWebsiteSummary';
import type { EmailMetrics } from '../../../api/results';
import type { LucideProps } from 'lucide-react';
import { formatGoogleAdsSignal, formatRatingRange, ratingValueToPosition } from '../SearchForm/leadFilters';

const SHOW_GOOGLE_MAPS_REQUIREMENTS = false;

type CompletedSummaryStatItem = {
  label: string;
  value: number | string;
  suffix: string;
  helperText?: string;
  icon: React.ComponentType<LucideProps>;
};

const SearchDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const searchId = parseInt(id || '0', 10);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [successNotification, setSuccessNotification] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [emailMetrics, setEmailMetrics] = useState<EmailMetrics | null>(null);
  const [pipelineExpanded, setPipelineExpanded] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [emailMetricsLoading, setEmailMetricsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const hasInitialized = useRef(false);
  const previousSearchIdRef = useRef<number | null>(null);
  
  const { 
    currentSearch, 
    loading: searchLoading, 
    error: searchError, 
    fetchSearch, 
    deleteSearch, 
    clearError: clearSearchError,
    clearCurrentSearch
  } = useSearch();
  
  
  // Format error messages to ensure they're strings
  const formattedSearchError = searchError ? (typeof searchError === 'string' ? searchError : JSON.stringify(searchError)) : null;
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      searchParams.get('fromCreate') !== 'true' ||
      !currentSearch ||
      currentSearch.id !== searchId
    ) return;

    setSuccessNotification(getCreationNotificationMessage(currentSearch));
    searchParams.delete('fromCreate');
    const remainingSearch = searchParams.toString();
    navigate(
      `${location.pathname}${remainingSearch ? `?${remainingSearch}` : ''}`,
      { replace: true },
    );
  }, [currentSearch, location.pathname, location.search, navigate, searchId]);

  const fetchSearchCallback = useCallback(() => {
    if (searchId > 0 && isAuthenticated) {
      fetchSearch(searchId);
    } else if (!isAuthenticated) {
      console.log('SearchDetail: Not authenticated, skipping fetch');
    }
  }, [searchId, fetchSearch, isAuthenticated]);
  
  useEffect(() => {
    if (!isAuthenticated || searchId <= 0) {
      return;
    }

    const searchIdChanged = previousSearchIdRef.current !== null && previousSearchIdRef.current !== searchId;
    const currentSearchMismatch = currentSearch && currentSearch.id !== searchId;
    
    if (searchIdChanged || currentSearchMismatch) {
      clearCurrentSearch();
      setIsInitialLoading(true);
      hasInitialized.current = false;
    }

    if (!hasInitialized.current) {
      fetchSearchCallback();
      hasInitialized.current = true;
    }

    previousSearchIdRef.current = searchId;
  }, [searchId, isAuthenticated, fetchSearchCallback, clearCurrentSearch, currentSearch]);

  useEffect(() => {
    if (currentSearch && currentSearch.id === searchId && isInitialLoading) {
      setIsInitialLoading(false);
    }
    
    if (!searchLoading && !currentSearch && isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [currentSearch, isInitialLoading, searchId, searchLoading]);
  
  useEffect(() => {
    if (currentSearch && currentSearch.status === TaskStatus.COMPLETED && isAuthenticated) {
      setEmailMetricsLoading(true);
      import('../../../api/results').then(({ getEmailMetrics }) => {
        getEmailMetrics(searchId)
          .then((data) => { setEmailMetrics(data); setEmailMetricsLoading(false); })
          .catch((error) => {
            console.error('Failed to fetch email metrics:', error);
            setEmailMetrics(null);
            setEmailMetricsLoading(false);
          });
      });
    } else {
      setEmailMetrics(null);
      setEmailMetricsLoading(false);
    }
  }, [currentSearch, searchId, isAuthenticated]);
  
  useEffect(() => {
    if (!isAuthenticated || searchId <= 0) {
      return;
    }

    if (currentSearch && currentSearch.status === TaskStatus.COMPLETED) {
      return;
    }

    const interval = setInterval(() => {
      if (hasInitialized.current) {
        fetchSearchCallback();
      }
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [searchId, isAuthenticated, currentSearch, fetchSearchCallback]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchSearchCallback();
    } finally {
      setRefreshing(false);
    }
  };
  
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this search? This action cannot be undone.')) {
      await deleteSearch(searchId);
      navigate('/searches');
    }
  };
  
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Show a simpler message if not authenticated yet
  if (!isAuthenticated) {
    return (
      <Card>
        <div className="flex justify-center items-center py-12">
          <span className="text-gray-600">Please log in to view search details</span>
        </div>
      </Card>
    );
  }
  
  if (isInitialLoading && !currentSearch) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95">
        <div className="text-center">
          <div className="inline-block animate-bounce text-6xl mb-4">
            🚀
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Loading your search...
          </h3>
          <p className="text-gray-600">
            Please wait while we fetch your search details
          </p>
        </div>
      </div>
    );
  }
  
  if (emailMetricsLoading && currentSearch?.google_maps_requirements?.has_website !== false) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95">
        <div className="text-center">
          <div className="inline-block animate-bounce text-6xl mb-4">
            🚀
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Loading your search...
          </h3>
          <p className="text-gray-600">
            Please wait while we fetch your search details
          </p>
        </div>
      </div>
    );
  }

  if (searchLoading && !currentSearch && !isInitialLoading) {
    return (
      <Card>
        <div className="flex justify-center items-center py-12">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">Loading search details...</span>
        </div>
      </Card>
    );
  }
  
  if (!currentSearch) {
    return (
      <Card>
        <div className="text-center py-12">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900">Search not found</h3>
          <p className="mt-1 text-gray-500">The search you're looking for doesn't exist or you don't have access to it.</p>
          <div className="mt-6">
            <Link to="/searches">
              <Button>Back to Searches</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  const completedSummaryStats: CompletedSummaryStatItem[] = [
    {
      label: 'Businesses',
      value: currentSearch.places_saved ?? currentSearch.completed_links,
      suffix: 'found',
      icon: Building2,
    },
    {
      label: 'Decision makers',
      value: emailMetrics !== null ? emailMetrics.decision_makers_count : '—',
      suffix: 'people found',
      helperText: 'ready to find emails',
      icon: User,
    },
  ];

  if (
    currentSearch.google_ads_signal === 'detected_recently'
    && currentSearch.google_ads_detected_count !== undefined
    && currentSearch.google_ads_detected_count !== null
    && currentSearch.google_ads_detected_count > 0
  ) {
    completedSummaryStats.push({
      label: 'Google Ads advertisers',
      value: currentSearch.google_ads_detected_count,
      suffix: 'detected & prioritized',
      helperText: 'show Google Ads tracking on their website',
      icon: Megaphone,
    });
  }

  const nextResultSteps = [
    {
      label: 'Browse',
      description: 'Filter by rating, email availability, and decision makers.',
      icon: Search,
    },
    {
      label: 'Verify',
      description: 'Confirm deliverability before outreach.',
      icon: CheckCircle,
    },
    {
      label: 'Find emails',
      description: 'Enrich key decision makers with direct email addresses.',
      icon: Mail,
    },
    {
      label: 'Export',
      description: 'Download a clean list when the results are ready.',
      icon: Download,
    },
  ];

  const pipeline = getSearchPipeline(currentSearch);
  const pipelineSteps = pipeline.steps;
  const isNoWebsiteSearch = pipeline.variant === 'no-website';
  const overallProgress = getSearchProgressPercent(currentSearch);
  const displayStatus = pipeline.failed
    ? 'FAILED'
    : pipeline.activeStep?.label || 'Pending';

  return (
    <div className="pt-4">
      {currentSearch && currentSearch.status !== TaskStatus.COMPLETED && (
        <SearchStepper pipeline={pipeline} />
      )}
      <div className="flex justify-between items-start gap-4 mb-4 flex-wrap">
        {currentSearch.status === TaskStatus.COMPLETED ? (
          <nav className="text-sm text-gray-500 flex items-center gap-2 flex-wrap min-w-0" aria-label="Breadcrumb">
            <Link to="/searches" className="hover:text-gray-900 shrink-0">
              Searches
            </Link>
            <span aria-hidden className="shrink-0">
              ›
            </span>
            <span className="text-gray-900 font-medium truncate">
              {currentSearch.search_query}
            </span>
          </nav>
        ) : (
          <h2 className="text-xl font-semibold text-gray-800">
            Search: {currentSearch.search_query}
          </h2>
        )}
        <div className="flex items-center space-x-3 shrink-0 ml-auto">
          <Button
            variant="secondary"
            onClick={handleRefresh}
            isLoading={refreshing}
            className="flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </Button>
          <Link to="/searches">
            <Button
              variant="secondary"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to search list</span>
            </Button>
          </Link>
        </div>
      </div>
      
      {formattedSearchError && (
        <Notification
          type="error"
          message={formattedSearchError}
          onClose={clearSearchError}
        />
      )}
      
      {successNotification && (
        <Notification
          type="success"
          message={successNotification}
          onClose={() => setSuccessNotification(null)}
        />
      )}

      <div className="flex justify-center">
        <div className="w-full">
          {currentSearch.status === TaskStatus.COMPLETED ? (
            <Card className="mb-6">
              <div className="p-6">
                {isNoWebsiteSearch ? (
                  <CompletedNoWebsiteSummary
                    search={currentSearch}
                    dateLabel={formatShortDate(currentSearch.created_at)}
                    emailMetrics={emailMetrics}
                    pipelineSteps={pipelineSteps}
                    pipelineExpanded={pipelineExpanded}
                    onTogglePipeline={() => setPipelineExpanded((previous) => !previous)}
                  />
                ) : (
                  <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase text-gray-500">
                      <span>Search result</span>
                      <span className="h-1 w-1 rounded-full bg-gray-300" />
                      <span className="normal-case">{formatShortDate(currentSearch.created_at)}</span>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 mt-1 break-words">{currentSearch.search_query}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {currentSearch.city?.display_name ?? '—'}
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-200 px-3 py-1.5 text-xs font-semibold text-green-800 shrink-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Completed
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-4">
                  {completedSummaryStats.map((stat) => {
                    const StatIcon = stat.icon;

                    return (
                      <div key={stat.label} className="rounded-lg border border-gray-200 bg-white px-4 py-3.5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                            <p className="mt-1 text-3xl font-semibold leading-none text-primary tabular-nums">
                              {stat.value}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">{stat.suffix}</p>
                            {stat.helperText ? (
                              <p className="mt-0.5 text-xs font-bold text-gray-500">{stat.helperText}</p>
                            ) : null}
                          </div>
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <StatIcon className="h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 mb-6">
                  <div className="flex flex-col gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Info className="h-4 w-4" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        Turn results into outreach-ready leads
                      </p>
                    </div>
                    <div className="grid min-w-0 w-full grid-cols-2 gap-2 sm:grid-cols-4">
                      {nextResultSteps.map((step, index) => {
                        const StepIcon = step.icon;

                        return (
                          <div key={step.label} className="min-w-0">
                            <HoverTooltip
                              content={step.description}
                              wrapperClassName="flex w-full min-w-0"
                            >
                              <div className="flex min-h-[44px] w-full min-w-0 cursor-help items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                                  {index + 1}
                                </span>
                                <StepIcon className="h-4 w-4 shrink-0 text-primary" />
                                <span className="min-w-0 break-words leading-snug">{step.label}</span>
                              </div>
                            </HoverTooltip>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <HoverTooltip content="Verify and enrich inside before exporting">
                    <Link to={`/searches/${currentSearch.id}/results`}>
                      <Button className="inline-flex items-center gap-2">
                        <Search className="h-4 w-4" />
                        View results
                      </Button>
                    </Link>
                  </HoverTooltip>
                  <button
                    type="button"
                    onClick={() => setPipelineExpanded((previous) => !previous)}
                    className="sm:ml-auto inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
                  >
                    {pipelineExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {pipelineExpanded ? 'Hide pipeline' : 'Pipeline details'}
                  </button>
                </div>

                {pipelineExpanded && (
                  <div id="completed-search-pipeline" className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center w-full min-w-0 overflow-x-auto pb-2 gap-0">
                      {pipelineSteps.map((step, idx) => (
                        <React.Fragment key={step.id}>
                          <div className="flex flex-col items-center min-w-[68px] shrink-0">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span className="text-[10px] text-gray-500 text-center mt-1 leading-tight max-w-[72px]">
                              {step.compactLabel}
                            </span>
                          </div>
                          {idx < pipelineSteps.length - 1 && (
                            <div className="h-0.5 flex-1 bg-primary min-w-[12px] mb-5 shrink" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
                  </>
                )}
              </div>
            </Card>
          ) : (
            <Card className="mb-6">
              <div className="flex flex-col items-center justify-center py-10 px-6">
                <div className="relative w-16 h-16 mb-7">
                  {!pipeline.failed && (
                    <div
                      className="absolute rounded-full border-2 animate-spin motion-reduce:animate-none"
                      style={{
                        inset: '-8px',
                        borderColor: 'transparent',
                        borderTopColor: 'rgba(138,48,51,0.55)',
                        borderRightColor: 'rgba(138,48,51,0.18)',
                        animationDuration: '2s',
                      }}
                    />
                  )}
                  <div className={`absolute inset-0 rounded-full border flex items-center justify-center ${
                    pipeline.failed
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-primary/10 bg-primary/5 text-primary'
                  }`}>
                    {pipeline.failed ? <X className="h-7 w-7" /> : <Search className="h-7 w-7" />}
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                  {pipeline.failed
                    ? 'Search stopped'
                    : pipeline.activeStep?.id === 'pending'
                      ? 'Search queued'
                      : 'Search in progress'}
                </h3>
                <p aria-live="polite" className="text-sm text-gray-600 text-center max-w-sm leading-relaxed mb-7">
                  {pipeline.failed
                    ? pipeline.supportingText
                    : pipeline.activeStep?.description || pipeline.supportingText}
                </p>

                <div className="w-full max-w-sm border border-gray-200 rounded-xl p-5 mb-6 bg-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Search progress</p>
                  <div className="flex flex-col gap-3">
                    {pipelineSteps.map((stage) => (
                      <div
                        key={stage.id}
                        aria-current={stage.state === 'active' ? 'step' : undefined}
                        className="flex items-start gap-3"
                      >
                        {stage.state === 'done' ? (
                          <div className="w-4 h-4 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg width="8" height="8" fill="none" viewBox="0 0 8 8">
                              <path d="M1.5 4L3.5 6L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        ) : stage.state === 'active' ? (
                          <div
                            className="w-4 h-4 rounded-full border-2 animate-spin motion-reduce:animate-none flex-shrink-0 mt-0.5"
                            style={{ borderColor: '#D97706', borderTopColor: 'transparent' }}
                          />
                        ) : (
                          <div className="w-4 h-4 rounded-full bg-gray-100 border border-gray-300 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div>
                            <span className={`text-sm ${
                              stage.state === 'active'
                                ? 'text-gray-900 font-semibold'
                                : stage.state === 'done'
                                  ? 'text-gray-700 font-medium'
                                  : 'text-gray-500'
                            }`}>
                              {stage.label}
                            </span>
                          </div>
                          {stage.state === 'active' && (
                            <span className="text-xs text-gray-600 block mt-1">{pipeline.supportingText}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Updates automatically · Typical search: 40–85 min · Safe to close this tab
                </p>
              </div>

              {currentSearch.is_queued && (
                <QueueInfo
                  queuePosition={currentSearch.queue_position || 0}
                  estimatedWaitMinutes={currentSearch.estimated_wait_minutes || 0}
                  variant="detailed"
                />
              )}

              {!pipeline.failed && (
                <div className="px-6 pb-5" aria-live="polite">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs text-gray-600">Overall progress</span>
                    <span className="text-xs font-semibold tabular-nums text-primary">{overallProgress}%</span>
                  </div>
                  <div className="overflow-hidden h-1.5 rounded-full bg-gray-100">
                    <div
                      style={{ width: `${overallProgress}%` }}
                      className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none"
                    />
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Search Details */}
          <Card className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Search details</h3>
            {currentSearch.status === TaskStatus.COMPLETED ? (
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <div className="grid grid-cols-2 gap-px bg-gray-200">
                  <div className="bg-white p-4">
                    <p className="text-xs text-gray-500">Query</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{currentSearch.search_query}</p>
                  </div>
                  <div className="bg-white p-4">
                    <p className="text-xs text-gray-500">Status</p>
                    <p className="mt-0.5">
                      <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800">
                        {currentSearch.status}
                      </span>
                    </p>
                  </div>
                  <div className="bg-white p-4">
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {currentSearch.city?.display_name || 'Location data unavailable'}
                    </p>
                  </div>
                  <div className="bg-white p-4">
                    <p className="text-xs text-gray-500">Created at</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {formatDate(currentSearch.created_at)} <b>UTC</b>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 text-gray-400">
                  <Search className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-500">Query</p>
                  <p className="mt-1 text-sm text-gray-900">{currentSearch.search_query}</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 text-gray-400">
                  <Clock className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      currentSearch.status === TaskStatus.PENDING
                        ? 'bg-gray-100 text-gray-800'
                        : currentSearch.status === TaskStatus.QUEUED
                        ? 'bg-blue-100 text-blue-800'
                        : currentSearch.status === TaskStatus.PROCESSING
                        ? 'bg-blue-100 text-blue-800'
                        : currentSearch.status === TaskStatus.FAILED
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {displayStatus}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 text-gray-400">
                  <Map className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {currentSearch.city?.display_name || 'Location data unavailable'}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 text-gray-400">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-500">Coordinates</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {typeof currentSearch.lat === 'number' && typeof currentSearch.lng === 'number'
                      ? `${currentSearch.lat.toFixed(6)}, ${currentSearch.lng.toFixed(6)}`
                      : 'Coordinate data unavailable'}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 text-gray-400">
                  <Calendar className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-500">Created At</p>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(currentSearch.created_at)} <b>UTC</b></p>
                </div>
              </div>
            </div>
            )}

            {currentSearch.website_filter && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <p className="text-sm font-semibold text-gray-900">Website match filter</p>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">
                  We checked business websites for{' '}
                  {Object.keys(currentSearch.website_filter.offering_tags).length > 0 && currentSearch.website_filter.customer_tags.length > 0
                    ? 'these services and customer types'
                    : Object.keys(currentSearch.website_filter.offering_tags).length > 0
                      ? 'these services'
                      : currentSearch.website_filter.customer_tags.length > 0
                        ? 'these customer types'
                        : 'your targeting'}
                  {', then ranked website matches first.'}
                </p>
                {Object.keys(currentSearch.website_filter.offering_tags).length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Services</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.values(currentSearch.website_filter.offering_tags).map(label => (
                        <span key={label} className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">{label}</span>
                      ))}
                    </div>
                  </div>
                )}
                {currentSearch.website_filter.customer_tags.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Customer types</p>
                    <div className="flex flex-wrap gap-1.5">
                      {currentSearch.website_filter.customer_tags.map(tag => (
                        <span key={tag} className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-800">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(
              (currentSearch.google_maps_requirements && (
                currentSearch.google_maps_requirements.has_website != null ||
                currentSearch.google_maps_requirements.min_review_rating != null ||
                currentSearch.google_maps_requirements.max_review_rating != null
              )) || currentSearch.google_ads_signal != null
            ) && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <p className="text-sm font-semibold text-gray-900">Lead filters</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {currentSearch.google_maps_requirements?.has_website != null && (
                    <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-800">
                      {currentSearch.google_maps_requirements.has_website ? 'Has a website' : 'No website'}
                    </span>
                  )}
                  {(currentSearch.google_maps_requirements?.min_review_rating != null || currentSearch.google_maps_requirements?.max_review_rating != null) && (
                    <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-800">
                      {formatRatingRange(
                        ratingValueToPosition(currentSearch.google_maps_requirements?.min_review_rating ?? 0),
                        ratingValueToPosition(currentSearch.google_maps_requirements?.max_review_rating ?? 5),
                      )}
                    </span>
                  )}
                  {currentSearch.google_ads_signal != null && (
                    <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-800">
                      {formatGoogleAdsSignal(currentSearch.google_ads_signal)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {SHOW_GOOGLE_MAPS_REQUIREMENTS && currentSearch.google_maps_requirements && Object.keys(currentSearch.google_maps_requirements).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-3">Google Maps requirements</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {currentSearch.google_maps_requirements.min_review_count != null && (
                    <span className="text-gray-700">Min reviews: <strong>{currentSearch.google_maps_requirements.min_review_count}</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.max_review_count != null && (
                    <span className="text-gray-700">Max reviews: <strong>{currentSearch.google_maps_requirements.max_review_count}</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.min_review_rating != null && (
                    <span className="text-gray-700">Min rating: <strong>{currentSearch.google_maps_requirements.min_review_rating}</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.max_review_rating != null && (
                    <span className="text-gray-700">Max rating: <strong>{currentSearch.google_maps_requirements.max_review_rating}</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.has_phone_number === true && (
                    <span className="text-gray-700">Has phone: <strong>Require</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.has_phone_number === false && (
                    <span className="text-gray-700">Has phone: <strong>Exclude</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.has_website === true && (
                    <span className="text-gray-700">Has website: <strong>Require</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.has_website === false && (
                    <span className="text-gray-700">Has website: <strong>Exclude</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.temporary_closed === true && (
                    <span className="text-gray-700">Temporary closed: <strong>Only closed</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.temporary_closed === false && (
                    <span className="text-gray-700">Temporary closed: <strong>Must be open</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.lgbtq_friendly === true && (
                    <span className="text-gray-700">LGBTQ+ friendly: <strong>Require</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.lgbtq_friendly === false && (
                    <span className="text-gray-700">LGBTQ+ friendly: <strong>Exclude</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.latino_owned === true && (
                    <span className="text-gray-700">Latino-owned: <strong>Require</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.latino_owned === false && (
                    <span className="text-gray-700">Latino-owned: <strong>Exclude</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.asian_owned === true && (
                    <span className="text-gray-700">Asian-owned: <strong>Require</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.asian_owned === false && (
                    <span className="text-gray-700">Asian-owned: <strong>Exclude</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.veteran_owned === true && (
                    <span className="text-gray-700">Veteran-owned: <strong>Require</strong></span>
                  )}
                  {currentSearch.google_maps_requirements.veteran_owned === false && (
                    <span className="text-gray-700">Veteran-owned: <strong>Exclude</strong></span>
                  )}
                </div>
              </div>
            )}
          </Card>

          {currentSearch.status === TaskStatus.COMPLETED && (
            <div className="mb-6">
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 text-sm text-red-700 underline-offset-2 underline hover:text-red-800 bg-transparent border-0 p-0 cursor-pointer"
              >
                <Trash2 className="h-3 w-3 shrink-0" strokeWidth={2} />
                Delete this search
              </button>
            </div>
          )}

          {/* Search Area */}
          <Card className="mb-6">
            {/* Map showing the search area */}
            {currentSearch.city && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">Search Area</h4>
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <BoundingBoxMap
                    key={`search-${currentSearch.id}-${currentSearch.city.lat}-${currentSearch.city.lng}`}
                    lat={currentSearch.lat || currentSearch.city.lat}
                    lng={currentSearch.lng || currentSearch.city.lng}
                    lat_start={currentSearch.city.lat_start}
                    long_start={currentSearch.city.long_start}
                    lat_end={currentSearch.city.lat_end}
                    long_end={currentSearch.city.long_end}
                    radius={currentSearch.radius}
                    display_name={currentSearch.city.display_name}
                    height="300px"
                    forceRefresh={true}
                    disableWarning={false}
                  />
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SearchDetail; 
